import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { BarChart3, Flame, Snowflake, TrendingUp } from "lucide-react";

export default function Analytics() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get("/predict/number", { params: { limit: 30 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  return (
    <>
      <PageHeader title="Analytics" subtitle="Frequency and trend analysis of recent results" />
      <div className="max-w-xl mx-auto w-full px-4 pb-10 space-y-4">
        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="h-4 w-4 text-indigo-600" />
            <span className="text-xs font-bold uppercase tracking-widest text-slate-600">
              Sample size · {data?.sample_size ?? 0} rounds
            </span>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            Statistics computed from the last 30 published F/R and S/R results.
          </p>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="h-4 w-4 text-rose-600" />
            <h3 className="font-heading font-bold">Hot Digits</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {(data?.hot_digits || []).map((d) => (
              <span
                key={d}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-rose-500 text-white font-numbers font-black text-xl"
              >
                {d}
              </span>
            ))}
            {(!data?.hot_digits || data.hot_digits.length === 0) && (
              <span className="text-slate-500 text-sm">Not enough data yet.</span>
            )}
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <Snowflake className="h-4 w-4 text-sky-600" />
            <h3 className="font-heading font-bold">Cold Digits</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {(data?.cold_digits || []).map((d) => (
              <span
                key={d}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100 text-sky-800 font-numbers font-black text-xl"
              >
                {d}
              </span>
            ))}
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <h3 className="font-heading font-bold">Most Frequent Pairs</h3>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {(data?.hot_pairs || []).map((p) => (
              <div
                key={p.number}
                className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-center"
              >
                <div className="font-numbers font-black text-2xl text-slate-900">{p.number}</div>
                <div className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">
                  ×{p.count}
                </div>
              </div>
            ))}
            {(!data?.hot_pairs || data.hot_pairs.length === 0) && (
              <div className="col-span-4 text-slate-500 text-sm text-center py-2">
                Not enough data yet.
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
